import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Flex, Modal, notify } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import componentService from 'packages/services/componentService';
import componentCollectionService from 'packages/services/componentCollectionService';

type DeleteType = 'collection' | 'component';

interface ComponentDeleteModalProps {
    onDeleteSuccess: (resetCollection: boolean) => void;
}

const useComponentDeleteModal = ({ onDeleteSuccess }: ComponentDeleteModalProps) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [deleteType, setDeleteType] = useState<DeleteType>('component');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isConsentChecked, setIsConsentChecked] = useState(false);

    const translations = useMemo(() => {
        const prefix = deleteType === 'collection' ? 'components.collection.delete.modal' : 'components.delete.modal';

        return {
            title: translate(`${prefix}.title`),
            content: translate(`${prefix}.description`),
            deleteButton: translate(`${prefix}.delete`),
            cancelButton: translate(`${prefix}.cancel`),
            successMessage: translate(`${prefix}.success`),
            errorMessage: translate(`${prefix}.error`),
        };
    }, [deleteType, translate]);

    const handleOpen = (id: number, type: DeleteType) => {
        setDeleteType(type);
        setSelectedId(id);
        setIsOpen(true);
    };

    const handleClose = () => {
        if (isDeleting) {
            return;
        }

        setIsOpen(false);

        // Clear selected ID after modal close animation
        setTimeout(() => {
            setSelectedId(null);
            setIsConsentChecked(false);
        }, 300);
    };

    const handleDelete = async () => {
        if (selectedId === null) {
            return;
        }

        setIsDeleting(true);

        try {
            const resp =
                deleteType === 'collection'
                    ? componentCollectionService.deleteComponentCollection(selectedId)
                    : componentService.deleteComponent(selectedId);

            if ((await resp).status === 200) {
                notify({
                    variant: 'success',
                    title: translations.successMessage,
                });
                onDeleteSuccess(deleteType === 'collection');
            } else {
                notify({
                    variant: 'error',
                    title: translations.errorMessage,
                });
            }

            handleClose();
        } finally {
            setTimeout(() => {
                setIsDeleting(false);
            }, 300);
        }
    };

    const renderDeleteModal = () => (
        <Modal
            title={translations.title}
            isOpen={isOpen}
            onClose={handleClose}
            actionPrimary={
                <Button
                    variant="danger"
                    onClick={handleDelete}
                    isLoading={isDeleting}
                    isDisabled={isDeleting || (deleteType === 'collection' && !isConsentChecked)}
                >
                    {translations.deleteButton}
                </Button>
            }
            actionSecondary={
                <Button variant="secondary" onClick={handleClose} isDisabled={isDeleting}>
                    {translations.cancelButton}
                </Button>
            }
        >
            <Flex direction="column" gap="5">
                {translations.content}
                {deleteType === 'collection' && (
                    <Checkbox isChecked={isConsentChecked} onChange={() => setIsConsentChecked(!isConsentChecked)}>
                        {translate('components.collection.delete.modal.consent')}
                    </Checkbox>
                )}
            </Flex>
        </Modal>
    );

    return { renderDeleteModal, onDeleteModalOpen: handleOpen };
};

export default useComponentDeleteModal;
